import { T, enabledSizes, tt } from '@agos/shared'
import { cn } from '@agos/ui'
import { useEffect, useMemo, useState } from 'react'

import { SizesTab } from './SizesTab'
import { ToppingsTab } from './ToppingsTab'
import { usePizzaConfigSettings } from './usePizzaConfigSettings'

/**
 * Ported from legacy `pages/pizza-config.tsx`. Spec:
 * `docs/pages/manager-menu/pizza-config.md`.
 *
 * Mounted at `/pizza`. The toppings table's columns come from the *enabled*
 * sizes, so adding a size in the second tab widens the first (§14.6).
 */
export function PizzaConfigPage() {
  const { config, setConfig, save, isSaving, justSaved, hasUnsavedChanges } =
    usePizzaConfigSettings()
  const [activeTab, setActiveTab] = useState<'toppings' | 'sizes'>('toppings')

  const sizeColumns = useMemo(() => enabledSizes(config), [config])

  // FB-025: sizes, prices and switches are local until Uložiť nastavenia, and
  // legacy never warns on the way out.
  useEffect(() => {
    if (!hasUnsavedChanges) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [hasUnsavedChanges])

  const tabs = [
    { key: 'toppings' as const, label: tt('Suroviny') },
    { key: 'sizes' as const, label: tt('Nastavenia') },
  ]

  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        <T>Konfigurácia surovín a nastavení pre vlastnú pizzu.</T>
      </p>

      <div className="flex items-center gap-2 border-b border-border pb-0">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'px-5 py-2.5 font-bold text-sm border-b-2 -mb-px transition-all',
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </button>
        ))}
        {hasUnsavedChanges && (
          <span className="ml-auto text-xs font-semibold text-amber-500">
            <T>Neuložené nastavenia</T>
          </span>
        )}
      </div>

      {activeTab === 'toppings' ? (
        <ToppingsTab sizes={sizeColumns} />
      ) : (
        <SizesTab
          config={config}
          setConfig={setConfig}
          save={save}
          isSaving={isSaving}
          justSaved={justSaved}
          onGoToIngredients={() => setActiveTab('toppings')}
        />
      )}
    </div>
  )
}
